import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import {
  isPlacesConfigured, attachAutocomplete, findPlace,
} from '../lib/places'
import {
  collapseSheet, guessType, baseName, parseDelimited,
} from '../lib/stockistParse'

// ── Shared bits ────────────────────────────────────────────────────────────────
const TYPE_LABEL = { bottle_shop: 'Bottle shop', venue: 'Venue' }

// type-badge styling: wine-red (flagged) for bottle shops, ochre (voided) for venues.
function TypeBadge({ type }) {
  if (type === 'both') {
    return (
      <span style={{ display: 'inline-flex', gap: '0.3rem', flexWrap: 'wrap' }}>
        <span className="type-badge flagged">Bottle shop</span>
        <span className="type-badge voided">Venue</span>
      </span>
    )
  }
  const cls = type === 'venue' ? 'voided' : 'flagged'
  return <span className={`type-badge ${cls}`}>{TYPE_LABEL[type] || type}</span>
}

// Map the two Type checkboxes ↔ the stored value ('bottle_shop' | 'venue' | 'both').
function typeFromFlags(isBottle, isVenue) {
  if (isBottle && isVenue) return 'both'
  if (isVenue) return 'venue'
  if (isBottle) return 'bottle_shop'
  return ''            // neither ticked → invalid, blocked on submit
}

// ── Add / Edit modal ────────────────────────────────────────────────────────────
function StockistFormModal({ stockist, onClose, onSaved }) {
  const isEdit = !!stockist
  const [form, setForm] = useState(() => ({
    name:     stockist?.name     || '',
    suburb:   stockist?.suburb   || '',
    type:     stockist?.type     || 'bottle_shop',
    address:  stockist?.address  || '',
    lat:      stockist?.lat ?? '',
    lng:      stockist?.lng ?? '',
    place_id: stockist?.place_id || '',
    phone:    stockist?.phone    || '',
    notes:    stockist?.notes    || '',
    active:   stockist?.active ?? true,
  }))
  const [error, setError]   = useState('')
  const [saving, setSaving] = useState(false)
  const acRef = useRef(null)

  // Bind Google autocomplete to the name field (add mode; edit can re-search too).
  useEffect(() => {
    if (!isPlacesConfigured() || !acRef.current) return
    let cleanup
    attachAutocomplete(acRef.current, (p) => {
      setForm(f => ({
        ...f,
        name:     p.name || f.name,
        address:  p.address || f.address,
        suburb:   p.suburb || f.suburb,
        lat:      p.lat ?? f.lat,
        lng:      p.lng ?? f.lng,
        place_id: p.place_id || f.place_id,
        type:     f.type || guessType(p.name || ''),
      }))
    }).then(fn => { cleanup = fn }).catch(() => {})
    return () => { if (cleanup) cleanup() }
  }, [isEdit])

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!form.name.trim()) { setError('Name is required.'); return }
    if (!form.type) { setError('Pick at least one type (bottle shop and/or venue).'); return }
    setSaving(true)

    const row = {
      name:     form.name.trim(),
      suburb:   form.suburb.trim() || null,
      type:     form.type,
      address:  form.address.trim() || null,
      lat:      form.lat === '' ? null : Number(form.lat),
      lng:      form.lng === '' ? null : Number(form.lng),
      place_id: form.place_id.trim() || null,
      phone:    form.phone.trim() || null,
      notes:    form.notes.trim() || null,
      active:   form.active,
      source:   stockist?.source || 'manual',
    }

    const res = isEdit
      ? await supabase.from('stockists').update(row).eq('id', stockist.id)
      : await supabase.from('stockists').insert(row)

    setSaving(false)
    if (res.error) { setError(res.error.message); return }
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">{isEdit ? 'Edit stockist' : 'Add stockist'}</h3>
        {error && <div className="alert alert-error">{error}</div>}
        {!isPlacesConfigured() && (
          <div className="alert alert-warning">
            Google autocomplete is off (no API key set). Enter details manually,
            including lat/lng for the map pin.
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Business name {isPlacesConfigured() && '(type to search Google)'}</label>
            <input
              ref={acRef}
              className="form-input"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Old Bridge Cellars"
              autoFocus
            />
            <p className="form-hint">Selecting a Google result autofills address, suburb, coordinates and place ID.</p>
          </div>

          <div className="flex gap-md">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Type (one or both)</label>
              <div className="flex gap-md" style={{ paddingTop: '0.35rem' }}>
                <label className="flex-center gap-sm" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={form.type === 'bottle_shop' || form.type === 'both'}
                    onChange={e => set('type', typeFromFlags(e.target.checked, form.type === 'venue' || form.type === 'both'))}
                  />
                  Bottle shop
                </label>
                <label className="flex-center gap-sm" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={form.type === 'venue' || form.type === 'both'}
                    onChange={e => set('type', typeFromFlags(form.type === 'bottle_shop' || form.type === 'both', e.target.checked))}
                  />
                  Venue
                </label>
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Suburb</label>
              <input className="form-input" value={form.suburb} onChange={e => set('suburb', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" value={form.address} onChange={e => set('address', e.target.value)} />
          </div>

          <div className="flex gap-md">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Latitude</label>
              <input className="form-input" value={form.lat} onChange={e => set('lat', e.target.value)} inputMode="decimal" />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Longitude</label>
              <input className="form-input" value={form.lng} onChange={e => set('lng', e.target.value)} inputMode="decimal" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Phone (optional — powers the “Call” link)</label>
            <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="08 9000 0000" />
            <p className="form-hint">Our own data. Leave blank to fall back to the “See on Google” link.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Notes (admin-only, never shown publicly)</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <label className="flex-center gap-sm" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
            Active (visible on the public map)
          </label>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add stockist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Import modal (upload / paste → tick-box review → apply) ──────────────────────
function ImportModal({ existing, onClose, onApplied }) {
  const [review, setReview] = useState(null)  // { newRows, dropped, excluded, flagged }
  const [busy, setBusy]     = useState(false)
  const [error, setError]   = useState('')
  const [progress, setProgress] = useState('')
  const pasteRef = useRef(null)

  function buildReview(rows) {
    const { kept, excluded } = collapseSheet(rows)

    // Index existing DB stockists by exact name and by chain base name.
    const byName = new Map()
    const byBase = new Map()
    for (const s of existing) {
      byName.set(s.name.toLowerCase(), s)
      const b = baseName(s.name)
      if (!byBase.has(b)) byBase.set(b, [])
      byBase.get(b).push(s)
    }

    const newRows = [], flagged = []
    const sheetNames = new Set()

    for (const k of kept) {
      sheetNames.add(k.name.toLowerCase())
      const exact = byName.get(k.name.toLowerCase())
      const baseMatches = byBase.get(baseName(k.name)) || []

      if (exact) continue                 // already exists → untouched
      if (baseMatches.length > 1) {
        // Name maps to >1 existing branch → never auto-merge.
        flagged.push({ ...k, matches: baseMatches.map(m => m.name) })
        continue
      }
      newRows.push({ ...k, include: true })
    }

    // Dropped: active DB stockists absent from this sheet → offer deactivation.
    const dropped = existing
      .filter(s => s.active && !sheetNames.has(s.name.toLowerCase()))
      .map(s => ({ id: s.id, name: s.name, suburb: s.suburb, deactivate: false }))

    setReview({ newRows, dropped, excluded, flagged })
  }

  async function handleFile(e) {
    setError('')
    const file = e.target.files?.[0]
    if (!file) return
    const isXlsx = /\.xlsx?$/i.test(file.name)
    try {
      if (isXlsx) {
        const rows = await parseXlsx(file)
        buildReview(rows)
      } else {
        buildReview(parseDelimited(await file.text()))
      }
    } catch (err) {
      setError('Could not read that file. ' + err.message)
    }
  }

  function handlePaste() {
    const text = pasteRef.current?.value || ''
    if (!text.trim()) { setError('Paste some rows first.'); return }
    setError('')
    buildReview(parseDelimited(text))
  }

  function toggleNew(i)     { setReview(r => ({ ...r, newRows: r.newRows.map((x, j) => j === i ? { ...x, include: !x.include } : x) })) }
  function toggleDrop(i)    { setReview(r => ({ ...r, dropped: r.dropped.map((x, j) => j === i ? { ...x, deactivate: !x.deactivate } : x) })) }
  function setAllNew(v)     { setReview(r => ({ ...r, newRows: r.newRows.map(x => ({ ...x, include: v })) })) }

  async function apply() {
    setBusy(true); setError('')
    const toAdd  = review.newRows.filter(r => r.include)
    const toDrop = review.dropped.filter(r => r.deactivate)
    let added = 0, failedGeo = 0, deactivated = 0

    try {
      // 1. Geocode + insert new (auto-attempt; flag failures).
      for (let i = 0; i < toAdd.length; i++) {
        const r = toAdd[i]
        setProgress(`Geocoding ${i + 1}/${toAdd.length}: ${r.name}`)
        let place = null
        if (isPlacesConfigured()) {
          try { place = await findPlace([r.name, r.suburb, 'Western Australia'].filter(Boolean).join(', ')) }
          catch { place = null }
        }
        const row = {
          name:   r.name,
          type:   r.type || 'bottle_shop',
          source: 'distributor_import',
          active: true,
          suburb: place?.suburb || r.suburb || null,
        }
        if (place) {
          Object.assign(row, {
            address: place.address || null, lat: place.lat ?? null,
            lng: place.lng ?? null, place_id: place.place_id || null,
          })
        } else {
          row.notes = 'NEEDS MANUAL PLACEMENT — no Places match at import'
          failedGeo++
        }
        const res = await supabase.from('stockists').insert(row)
        if (res.error) throw new Error(res.error.message)
        added++
      }

      // 2. Deactivate dropped (never hard-delete).
      if (toDrop.length) {
        setProgress(`Deactivating ${toDrop.length}…`)
        const ids = toDrop.map(r => r.id)
        const res = await supabase.from('stockists').update({ active: false }).in('id', ids)
        if (res.error) throw new Error(res.error.message)
        deactivated = ids.length
      }

      setProgress('')
      onApplied({ added, failedGeo, deactivated })
    } catch (err) {
      setBusy(false); setProgress('')
      setError('Apply failed: ' + err.message)
    }
  }

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: 680, maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">Import distributor sheet</h3>
        {error && <div className="alert alert-error">{error}</div>}

        {!review && (
          <>
            <div className="form-group">
              <label className="form-label">Upload CSV or XLSX</label>
              <input className="form-input" type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />
              <p className="form-hint">Distributor format: repeated “stockist name, product” rows. The product column is ignored.</p>
            </div>
            <hr className="divider" />
            <div className="form-group">
              <label className="form-label">…or paste rows</label>
              <textarea ref={pasteRef} className="form-input" rows={6} placeholder="Old Bridge Cellars - Como (WA), Shiraz 2023&#10;Liberty Liquors (WA), Riesling 2025" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePaste}>Review</button>
            </div>
          </>
        )}

        {review && (
          <>
            {/* New in this sheet */}
            <section style={{ marginBottom: '1.25rem' }}>
              <div className="flex-between" style={{ marginBottom: '0.5rem' }}>
                <h4 style={{ fontFamily: 'var(--font-serif)' }}>New in this sheet ({review.newRows.length})</h4>
                {review.newRows.length > 0 && (
                  <span className="text-sm">
                    <button className="btn btn-secondary btn-sm" onClick={() => setAllNew(true)}>All</button>{' '}
                    <button className="btn btn-secondary btn-sm" onClick={() => setAllNew(false)}>None</button>
                  </span>
                )}
              </div>
              {review.newRows.length === 0 && <p className="text-sm text-muted">Nothing new.</p>}
              {review.newRows.map((r, i) => (
                <label key={i} className="flex-center gap-sm" style={{ padding: '0.35rem 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={r.include} onChange={() => toggleNew(i)} />
                  <span style={{ flex: 1 }}>{r.name}{r.suburb ? <span className="text-muted text-sm"> · {r.suburb}</span> : ''}</span>
                  <TypeBadge type={r.type} />
                </label>
              ))}
              {review.newRows.some(r => r.include) && isPlacesConfigured() && (
                <p className="form-hint">Ticked rows are geocoded via Google Places on apply; failures are inserted flagged for manual placement.</p>
              )}
            </section>

            {/* Multi-branch flagged */}
            {review.flagged.length > 0 && (
              <section style={{ marginBottom: '1.25rem' }}>
                <h4 style={{ fontFamily: 'var(--font-serif)' }}>Needs manual disambiguation ({review.flagged.length})</h4>
                <p className="text-sm text-muted" style={{ marginBottom: '0.4rem' }}>
                  These names match more than one existing branch — add the right branch manually instead of auto-merging.
                </p>
                {review.flagged.map((r, i) => (
                  <div key={i} className="alert alert-warning" style={{ marginBottom: '0.4rem' }}>
                    <strong>{r.name}</strong> → {r.matches.join(', ')}
                  </div>
                ))}
              </section>
            )}

            {/* Dropped from sheet */}
            <section style={{ marginBottom: '1.25rem' }}>
              <h4 style={{ fontFamily: 'var(--font-serif)' }}>Dropped from this sheet ({review.dropped.length})</h4>
              <p className="text-sm text-muted" style={{ marginBottom: '0.4rem' }}>
                Active stockists absent from the sheet. Tick to deactivate (hidden from the map, not deleted).
              </p>
              {review.dropped.length === 0 && <p className="text-sm text-muted">None.</p>}
              {review.dropped.map((r, i) => (
                <label key={i} className="flex-center gap-sm" style={{ padding: '0.35rem 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={r.deactivate} onChange={() => toggleDrop(i)} />
                  <span style={{ flex: 1 }}>{r.name}{r.suburb ? <span className="text-muted text-sm"> · {r.suburb}</span> : ''}</span>
                </label>
              ))}
            </section>

            {/* Excluded / prefiltered */}
            {review.excluded.length > 0 && (
              <section style={{ marginBottom: '1rem' }}>
                <h4 style={{ fontFamily: 'var(--font-serif)' }}>Excluded (not stockists) ({review.excluded.length})</h4>
                {review.excluded.map((r, i) => (
                  <p key={i} className="text-sm text-muted">— {r.rawName} <em>[{r.reason}]</em></p>
                ))}
              </section>
            )}

            {progress && <div className="alert alert-info">{progress}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={apply} disabled={busy}>
                {busy ? 'Applying…' : `Apply (${review.newRows.filter(r => r.include).length} add, ${review.dropped.filter(r => r.deactivate).length} deactivate)`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// XLSX support: load SheetJS from CDN on demand (keeps the app bundle small).
async function parseXlsx(file) {
  if (!window.XLSX) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js'
      s.onload = resolve; s.onerror = () => reject(new Error('Could not load the XLSX reader.'))
      document.head.appendChild(s)
    })
  }
  const buf = await file.arrayBuffer()
  const wb = window.XLSX.read(buf, { type: 'array' })
  const rows = []
  wb.SheetNames.forEach(n => {
    const sheet = window.XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, blankrows: false })
    sheet.forEach(r => rows.push(r.map(c => (c == null ? '' : String(c)))))
  })
  return rows
}

// ── Main tab ────────────────────────────────────────────────────────────────────
export default function StockistsTab() {
  const [rows, setRows]       = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [editing, setEditing] = useState(undefined) // undefined=closed, null=add, obj=edit
  const [importing, setImporting] = useState(false)
  const [flash, setFlash]     = useState('')

  const fetchRows = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('stockists').select('*').order('name')
    setRows(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchRows() }, [fetchRows])

  async function toggleActive(s) {
    // Optimistic flip.
    setRows(rs => rs.map(r => r.id === s.id ? { ...r, active: !r.active } : r))
    const { error } = await supabase.from('stockists').update({ active: !s.active }).eq('id', s.id)
    if (error) { setFlash('Could not update: ' + error.message); fetchRows() }
  }

  async function remove(s) {
    if (!window.confirm(`Delete “${s.name}” permanently? Use the Active toggle instead to just hide it.`)) return
    const { error } = await supabase.from('stockists').delete().eq('id', s.id)
    if (error) { setFlash('Delete failed: ' + error.message); return }
    fetchRows()
  }

  const visible = rows.filter(r => {
    if (typeFilter !== 'all' && r.type !== typeFilter && r.type !== 'both') return false
    const q = search.trim().toLowerCase()
    if (!q) return true
    return r.name.toLowerCase().includes(q) || (r.suburb || '').toLowerCase().includes(q)
  })

  const missingCoords = rows.filter(r => r.active && (r.lat == null || r.lng == null)).length

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>

  return (
    <>
      {flash && <div className="alert alert-info" onClick={() => setFlash('')}>{flash}</div>}

      <div className="card">
        <div className="flex-between" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 className="card-title" style={{ marginBottom: 0 }}>Stockists</h2>
            <p className="text-sm text-muted">
              {rows.length} total · {rows.filter(r => r.active).length} active
              {missingCoords > 0 && <> · <span className="expiry-soon">{missingCoords} active without map coordinates</span></>}
            </p>
          </div>
          <div className="flex gap-sm">
            <button className="btn btn-secondary btn-sm" onClick={() => setImporting(true)}>Import sheet</button>
            <button className="btn btn-primary btn-sm" onClick={() => setEditing(null)}>Add stockist</button>
          </div>
        </div>

        <div className="flex gap-sm" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
          <input
            className="form-input"
            style={{ maxWidth: 260 }}
            placeholder="Search name or suburb…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="form-select" style={{ maxWidth: 160 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            <option value="bottle_shop">Bottle shops</option>
            <option value="venue">Venues</option>
          </select>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Type</th><th>Suburb</th><th>Map</th>
                <th style={{ textAlign: 'center' }}>Active</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(s => (
                <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                  <td>
                    <p style={{ fontWeight: 500 }}>{s.name}</p>
                    {s.notes && /NEEDS MANUAL PLACEMENT/.test(s.notes) &&
                      <p className="text-sm expiry-urgent">⚑ needs manual placement</p>}
                  </td>
                  <td><TypeBadge type={s.type} /></td>
                  <td className="text-sm">{s.suburb || '—'}</td>
                  <td className="text-sm">
                    {s.lat != null && s.lng != null
                      ? <span className="text-muted">✓ pinned</span>
                      : <span className="expiry-urgent">no coords</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className={`status-badge dot ${s.active ? 'active' : 'inactive'}`}
                      style={{ border: 'none', cursor: 'pointer' }}
                      onClick={() => toggleActive(s)}
                      title="Toggle public visibility"
                    >
                      {s.active ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(s)}>Edit</button>{' '}
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--danger)' }} onClick={() => remove(s)}>Delete</button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={6} className="empty-state">No stockists match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== undefined && (
        <StockistFormModal
          stockist={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); fetchRows() }}
        />
      )}

      {importing && (
        <ImportModal
          existing={rows}
          onClose={() => setImporting(false)}
          onApplied={({ added, failedGeo, deactivated }) => {
            setImporting(false)
            setFlash(`Import applied — ${added} added${failedGeo ? ` (${failedGeo} need manual placement)` : ''}, ${deactivated} deactivated.`)
            fetchRows()
          }}
        />
      )}
    </>
  )
}
